import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "@/shared/services/queryClient";
import {
  walletsApi,
  generateIdempotencyKey,
  type ApplyWalletToOrderRequest,
  type ApproveWalletEntryRequest,
  type CreateWalletAdjustmentRequest,
  type CreateWalletCashOutRequest,
  type CreateWalletTopupRequest,
  type RejectWalletEntryRequest,
  type ReverseWalletEntryRequest,
} from "../services/walletsApi";

/** Query keys are kept loose so any wallet write invalidates the read tree. */
const walletKey = (customerId: string) => ["wallets", "customer", customerId] as const;
const statementKey = (customerId: string) => ["wallets", "statement", customerId] as const;
const pendingKey = ["wallets", "pending"] as const;

// ── Reads ───────────────────────────────────────────────────────────────────

export const useWalletByCustomer = (customerId: string | undefined) => {
  return useQuery({
    queryKey: customerId ? walletKey(customerId) : ["wallets", "customer", "none"],
    queryFn: () => walletsApi.getByCustomer(customerId!),
    enabled: !!customerId,
    // Balance must always reflect ledger truth — no staleness window.
    staleTime: 0,
    refetchOnMount: "always",
  });
};

export const useWalletStatement = (
  customerId: string | undefined,
  params?: { from?: string; to?: string; pageNumber?: number; pageSize?: number }
) => {
  return useQuery({
    queryKey: customerId ? [...statementKey(customerId), params] : ["wallets", "statement", "none"],
    queryFn: () => walletsApi.getStatement(customerId!, params),
    enabled: !!customerId,
    placeholderData: keepPreviousData,
    // Activity must always reflect the ledger — a new Approved row appearing
    // because of a cascade elsewhere should show up the moment the Wallet tab
    // is opened, without F5.
    staleTime: 0,
    refetchOnMount: "always",
  });
};

/**
 * Finance pending-approval queue. Polled every 60s so the sidebar badge and
 * the page stay live without manual refresh — cheap because the query is
 * pagination-aware and the response is small.
 */
export const usePendingWalletApprovals = (params?: {
  type?: string;
  customer?: string;
  pageNumber?: number;
  pageSize?: number;
}) => {
  return useQuery({
    queryKey: [...pendingKey, params],
    queryFn: () => walletsApi.getPending(params),
    refetchInterval: 60_000,
    placeholderData: keepPreviousData,
  });
};

// ── Writes ──────────────────────────────────────────────────────────────────

/**
 * Each mutation generates a fresh Idempotency-Key per call. Callers must not
 * cache the key — TanStack Query's built-in retry uses the same mutation arg
 * and would replay safely against this same key, which is the whole point.
 */

export const useCreateTopup = () => {
  return useMutation({
    mutationFn: (payload: CreateWalletTopupRequest) =>
      walletsApi.createTopup(payload, generateIdempotencyKey()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pendingKey });
      // Bump the customer's wallet query without knowing the customer id
      // inline — invalidate broadly by feature.
      queryClient.invalidateQueries({ queryKey: ["wallets"] });
    },
  });
};

export const useCreateCashOut = () => {
  return useMutation({
    mutationFn: (payload: CreateWalletCashOutRequest) =>
      walletsApi.createCashOut(payload, generateIdempotencyKey()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wallets"] });
    },
  });
};

export const useCreateAdjustment = () => {
  return useMutation({
    mutationFn: (payload: CreateWalletAdjustmentRequest) =>
      walletsApi.createAdjustment(payload, generateIdempotencyKey()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wallets"] });
    },
  });
};

export const useApplyWalletToOrder = () => {
  return useMutation({
    mutationFn: (payload: ApplyWalletToOrderRequest) =>
      walletsApi.applyToOrder(payload, generateIdempotencyKey()),
    onSuccess: (entry) => {
      queryClient.invalidateQueries({ queryKey: ["wallets"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["order-amendments", "history"] });
      queryClient.invalidateQueries({ queryKey: ["payments"] });
      if (entry.orderId) {
        queryClient.invalidateQueries({ queryKey: ["orders", entry.orderId] });
        queryClient.invalidateQueries({ queryKey: ["payments", "order", entry.orderId] });
      }
    },
  });
};

export const useApproveWalletEntry = () => {
  return useMutation({
    mutationFn: ({
      entryId,
      payload,
    }: {
      entryId: string;
      payload: ApproveWalletEntryRequest;
    }) => walletsApi.approve(entryId, payload, generateIdempotencyKey()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pendingKey });
      queryClient.invalidateQueries({ queryKey: ["wallets"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["order-amendments", "history"] });
      queryClient.invalidateQueries({ queryKey: ["payments"] });
      queryClient.invalidateQueries({ queryKey: ["sales-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["credit-notes"] });
      queryClient.invalidateQueries({ queryKey: ["journal-entries"] });
    },
  });
};

export const useRejectWalletEntry = () => {
  return useMutation({
    mutationFn: ({
      entryId,
      payload,
    }: {
      entryId: string;
      payload: RejectWalletEntryRequest;
    }) => walletsApi.reject(entryId, payload, generateIdempotencyKey()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pendingKey });
      queryClient.invalidateQueries({ queryKey: ["wallets"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["payments"] });
    },
  });
};

export const useReverseWalletEntry = () => {
  return useMutation({
    mutationFn: ({
      entryId,
      payload,
    }: {
      entryId: string;
      payload: ReverseWalletEntryRequest;
    }) => walletsApi.reverse(entryId, payload, generateIdempotencyKey()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pendingKey });
      queryClient.invalidateQueries({ queryKey: ["wallets"] });
    },
  });
};
